import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircleHeart, Satellite, Eye, Clock, ShieldCheck, AlertTriangle } from 'lucide-react';

const EXPERIENCES = [
  {
    id: 'nelson',
    href: '/experiencia',
    title: 'La historia de Nelson',
    subtitle: 'Una conversación de WhatsApp que podría salvar una vida',
    duration: '4 minutos',
    description:
      'Nelson contesta tu llamada y te cuenta cómo fue caer paso a paso en la adicción: del trago de fin de semana al consumo diario, del control aparente al fondo. Una historia real, contada como un chat, que termina con un giro que nadie ve venir.',
    bullets: [
      'Las 5 fases del consumo, contadas en primera persona',
      'Estadísticas reales sobre adicción en Chile',
      'Líneas de ayuda 24/7 disponibles antes y después',
    ],
    icon: MessageCircleHeart,
    accent: 'emerald',
    image: '/og-experiencia-nelson.jpg',
    cta: 'Conocer a Nelson',
    warning: 'Aborda temas sensibles de salud mental.',
  },
  {
    id: 'yo-futuro',
    href: '/yo-del-futuro',
    title: 'Yo del Futuro',
    subtitle: 'Un mensaje desde 2030 — para ti, hoy',
    duration: '5 minutos',
    description:
      'Hoy interceptamos un mensaje. Es de tu yo del 2030, contándote en qué te convertiste si nunca pediste ayuda. Test interactivo basado en DSM-5 que evalúa tu nivel de dependencia y te entrega una recomendación clínica personalizada.',
    bullets: [
      'Test de 5 preguntas con base clínica DSM-5',
      'Recomendación personalizada de tratamiento',
      'Cotización pre-llenada según tu caso',
    ],
    icon: Satellite,
    accent: 'cyan',
    image: '/assets/experiencias/card_yo_futuro.jpg',
    cta: 'Recibir el mensaje',
    warning: null,
  },
  {
    id: 'espejo',
    href: '/espejo',
    title: 'Mírate en 5 años',
    subtitle: 'Sube tu foto y mira en qué te puede convertir el consumo',
    duration: '3 minutos',
    description:
      'Sube tu selfie. Una IA generará cómo te verías tras 5 años de adicción severa según la sustancia que consumes. Mientras se procesa, recibirás mensajes psicoeducativos sobre la enfermedad. El resultado puede sorprenderte.',
    bullets: [
      'Generación visual con IA basada en patrones reales',
      'Tu foto NO se guarda — se procesa y se descarta',
      'Funciona para alcohol, cocaína, pasta base y más',
    ],
    icon: Eye,
    accent: 'red',
    image: '/assets/experiencias/card_espejo.jpg',
    cta: 'Comenzar simulación',
    warning: 'Contiene imágenes simuladas que pueden impactar.',
  },
];

const ACCENT_CLASSES = {
  emerald: {
    border: 'border-emerald-500/40 hover:border-emerald-400',
    iconBg: 'bg-emerald-500/15',
    iconColor: 'text-emerald-400',
    cta: 'bg-emerald-500 hover:bg-emerald-400 text-black',
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    glow: 'hover:shadow-[0_0_60px_rgba(16,185,129,0.25)]',
  },
  cyan: {
    border: 'border-cyan-500/40 hover:border-cyan-400',
    iconBg: 'bg-cyan-500/15',
    iconColor: 'text-cyan-400',
    cta: 'bg-cyan-500 hover:bg-cyan-400 text-black',
    badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
    glow: 'hover:shadow-[0_0_60px_rgba(6,182,212,0.25)]',
  },
  red: {
    border: 'border-red-500/40 hover:border-red-400',
    iconBg: 'bg-red-500/15',
    iconColor: 'text-red-400',
    cta: 'bg-red-500 hover:bg-red-400 text-black',
    badge: 'bg-red-500/20 text-red-300 border-red-500/40',
    glow: 'hover:shadow-[0_0_60px_rgba(239,68,68,0.25)]',
  },
};

export default function ExperienciasLanding() {
  return (
    <>
      
        <title>Experiencias inmersivas: 3 historias que pueden cambiarte | SinAdicciones.org</title>
        <meta
          name="description"
          content="Tres experiencias inmersivas que muestran la realidad de la adicción. La historia de Nelson, Yo del Futuro y Mírate en 5 años. Gratis y sin registro."
        />
        <link rel="canonical" href="https://sinadicciones.org/experiencias" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/experiencias" />
        <meta property="og:title" content="3 experiencias inmersivas que pueden cambiar tu perspectiva sobre la adicción" />
        <meta property="og:description" content="Nelson, Yo del Futuro y Mírate en 5 años: tres historias hechas con IA y testimonios reales. Gratis y sin registro." />
        <meta property="og:image" content="https://sinadicciones.org/assets/experiencias/card_yo_futuro.jpg" />
        <meta property="og:image:secure_url" content="https://sinadicciones.org/assets/experiencias/card_yo_futuro.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="746" />
        <meta property="og:image:alt" content="Experiencias inmersivas sobre adicción - SinAdicciones.org" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="3 experiencias inmersivas sobre adicción" />
        <meta name="twitter:description" content="Nelson, Yo del Futuro y Mírate en 5 años. Gratis y sin registro." />
        <meta name="twitter:image" content="https://sinadicciones.org/assets/experiencias/card_yo_futuro.jpg" />
      

      <main className="min-h-screen bg-slate-950 text-slate-100 pt-24 pb-20" data-testid="experiencias-landing">
        <section className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-emerald-400 mb-4 font-mono">
              — EXPERIENCIAS INMERSIVAS —
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-5"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Tres formas de mirar<br className="hidden sm:block" />
              <span className="text-emerald-400">la adicción de frente.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 leading-relaxed">
              Estas no son páginas informativas. Son experiencias diseñadas para que vivas, en pocos minutos,
              lo que normalmente toma años entender. Gratis, sin registro, anónimas.
            </p>
          </motion.div>

          {/* Experience Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {EXPERIENCES.map((exp, idx) => {
              const a = ACCENT_CLASSES[exp.accent];
              const Icon = exp.icon;
              return (
                <motion.article
                  key={exp.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 + idx * 0.12 }}
                  className={`group relative bg-slate-900 border ${a.border} ${a.glow} rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1`}
                  data-testid={`experiencia-card-${exp.id}`}
                >
                  {/* Image */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-950">
                    <img
                      src={exp.image}
                      alt={exp.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${a.badge} border rounded-full text-[10px] font-mono uppercase tracking-wider`}>
                        <Clock className="w-3 h-3" />
                        {exp.duration}
                      </span>
                    </div>
                    <div className={`absolute bottom-3 right-3 w-12 h-12 rounded-xl ${a.iconBg} backdrop-blur-md flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${a.iconColor}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 sm:p-7 flex flex-col">
                    <h2
                      className="text-2xl text-white font-bold leading-tight mb-1.5"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {exp.title}
                    </h2>
                    <p className={`${a.iconColor} text-sm font-medium mb-4`}>
                      {exp.subtitle}
                    </p>
                    <p className="text-sm text-slate-400 leading-relaxed mb-5">
                      {exp.description}
                    </p>

                    <ul className="space-y-2 mb-6">
                      {exp.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                          <span className={`mt-1 w-1 h-1 rounded-full ${a.iconColor.replace('text-', 'bg-')} flex-shrink-0`} />
                          <span className="leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>

                    {exp.warning && (
                      <div className="mb-5 flex items-start gap-2 px-3 py-2 bg-amber-950/30 border border-amber-900/40 rounded-md">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-200 leading-relaxed">{exp.warning}</p>
                      </div>
                    )}

                    <Link
                      to={exp.href}
                      data-testid={`experiencia-cta-${exp.id}`}
                      className={`mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 ${a.cta} font-bold text-sm rounded-xl transition-all hover:scale-[1.02] group/btn`}
                    >
                      {exp.cta}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-16 sm:mt-20 max-w-3xl mx-auto"
          >
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
              <div className="flex items-start sm:items-center gap-4 flex-col sm:flex-row">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-1">100% privadas, 100% gratuitas</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    Las tres experiencias son anónimas y no requieren registro. Si necesitas hablar con un profesional, puedes
                    {' '}<Link to="/asesoria" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">solicitar asesoría experta</Link>
                    {' '}al final de cada una.
                  </p>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a href="tel:1412" className="flex items-center gap-2 px-3 py-2 bg-slate-950 rounded-lg hover:bg-slate-900 transition-colors group">
                  <span className="text-emerald-300 font-bold text-sm group-hover:text-emerald-200">SENDA · 1412</span>
                  <span className="text-xs text-slate-500">24/7</span>
                </a>
                <a href="tel:6003607777" className="flex items-center gap-2 px-3 py-2 bg-slate-950 rounded-lg hover:bg-slate-900 transition-colors group">
                  <span className="text-emerald-300 font-bold text-sm group-hover:text-emerald-200">Salud Responde</span>
                  <span className="text-xs text-slate-500">600 360 7777</span>
                </a>
                <a href="tel:131" className="flex items-center gap-2 px-3 py-2 bg-slate-950 rounded-lg hover:bg-slate-900 transition-colors group">
                  <span className="text-emerald-300 font-bold text-sm group-hover:text-emerald-200">SAMU · 131</span>
                  <span className="text-xs text-slate-500">Emergencia</span>
                </a>
              </div>
            </div>
          </motion.div>
        </section>
      </main>
    </>
  );
}
